import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, TrendingUp, Users, Clock, MapPin, BarChart3, DollarSign, Activity } from 'lucide-react';
import api from '../../lib/api';

const GOLD = '#D4AF37';

/**
 * Live + post-mortem analytics for a single event. Works for events with OR
 * without a venue map. Renders 6 cards:
 *  1. Sales by hour (last 72h)  — rush detection
 *  2. Sales by day  (last 30d)  — early adoption / late surge
 *  3. Sales by tier (tandas)    — which option converts best
 *  4. Audience demographics     — gender, age, cities, top RRPP
 *  5. Top buyers                — who spent the most
 *  6. Access timeline           — live check-ins on event day
 */
export default function HeatmapTab({ eventId, apiMode = 'producer' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const url = apiMode === 'admin'
          ? `/events/${eventId}/heatmap`
          : `/producers/me/events/${eventId}/heatmap`;
        const r = await api.get(url);
        if (mounted) setData(r.data);
      } catch (_) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    // Refresh every 60s for live monitoring
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [eventId, apiMode]);

  if (loading) return <div className="text-center py-16"><Loader2 className="mx-auto animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-500">No se pudo cargar el heatmap.</div>;

  const totalSold = (data.sales_by_tier || []).reduce((a, t) => a + (t.sold || 0), 0);
  const totalRevenue = (data.sales_by_tier || []).reduce((a, t) => a + (t.revenue || 0), 0);

  return (
    <div className="space-y-6" data-testid="heatmap-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Activity size={16} style={{ color: GOLD }} /> Heatmap del evento
          </h3>
          <p className="text-xs text-zinc-500 mt-1">Se actualiza cada 60 segundos · datos en vivo</p>
        </div>
        <div className="flex gap-3">
          <KPI label="Vendidas" value={totalSold} />
          <KPI label="Recaudado" value={`$${formatNumber(totalRevenue)}`} accent />
          <KPI label="Check-ins" value={`${data.access_summary?.checked_in || 0}/${data.access_summary?.sold || 0}`} />
        </div>
      </div>

      {/* Sales by hour */}
      <Card title="Rush de compra (últimas 72h)" icon={Clock}>
        {data.sales_by_hour?.length === 0 ? (
          <Empty>Todavía no hubo ventas este período</Empty>
        ) : (
          <BarChart
            items={data.sales_by_hour}
            labelKey="hour"
            valueKey="count"
            formatLabel={(h) => h.slice(11, 13) + 'h ' + h.slice(8, 10) + '/' + h.slice(5, 7)}
          />
        )}
      </Card>

      {/* Sales by day */}
      <Card title="Ventas por día (últimos 30 días)" icon={TrendingUp}>
        {data.sales_by_day?.length === 0 ? (
          <Empty>Sin ventas registradas</Empty>
        ) : (
          <BarChart
            items={data.sales_by_day}
            labelKey="date"
            valueKey="count"
            formatLabel={(d) => d.slice(8, 10) + '/' + d.slice(5, 7)}
            showRevenue
          />
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales by tier */}
        <Card title="Performance por tanda" icon={BarChart3}>
          {data.sales_by_tier?.length === 0 ? <Empty>Sin tandas con ventas</Empty> : (
            <div className="space-y-2.5">
              {data.sales_by_tier.map((t) => {
                const pct = t.total > 0 ? Math.min(100, (t.sold / t.total) * 100) : 100;
                return (
                  <div key={t.tier_id} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-white font-bold">{t.name}</span>
                      <span className="text-zinc-400">{t.sold}{t.total ? `/${t.total}` : ''} · ${formatNumber(t.revenue || 0)}</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: GOLD }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Demographics */}
        <Card title="Audiencia" icon={Users}>
          <Demographics demo={data.audience_demographics} />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top cities + RRPP */}
        <Card title="Origen y RRPP" icon={MapPin}>
          <TwoCols
            left={{ title: 'Top ciudades', data: data.audience_demographics?.top_cities || {} }}
            right={{ title: 'Top RRPP', data: data.audience_demographics?.by_rrpp || {} }}
          />
        </Card>

        {/* Top buyers */}
        <Card title="Top compradores" icon={DollarSign}>
          {data.top_buyers?.length === 0 ? <Empty>Sin compradores aún</Empty> : (
            <ul className="space-y-2">
              {data.top_buyers.slice(0, 6).map((b, i) => (
                <li key={b.email} className="flex items-center gap-3 text-sm">
                  <span className="w-5 text-center font-black text-zinc-500">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold truncate">{b.name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{b.email} · {b.orders} compra{b.orders === 1 ? '' : 's'}</p>
                  </div>
                  <span className="font-black tabular-nums" style={{ color: GOLD }}>${formatNumber(b.total_spent)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Access timeline */}
      <Card title="Ingresos al evento (en vivo)" icon={Activity}>
        {(data.access_timeline?.length || 0) === 0 ? (
          <Empty>Aún no hubo ingresos validados con el escáner</Empty>
        ) : (
          <BarChart
            items={data.access_timeline}
            labelKey="hour"
            valueKey="checkins"
            formatLabel={(h) => h.slice(11, 13) + 'h'}
          />
        )}
      </Card>
    </div>
  );
}

// ===================== UI building blocks =====================

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
      <h4 className="text-xs uppercase tracking-wider font-bold text-zinc-300 flex items-center gap-2 mb-4">
        {Icon ? <Icon size={13} style={{ color: GOLD }} /> : null} {title}
      </h4>
      {children}
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl px-3 py-2 border ${accent ? 'border-[#D4AF37]/40 bg-[#D4AF37]/8' : 'border-zinc-800 bg-zinc-900/60'}`}>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p
        className="font-black tabular-nums leading-none mt-0.5"
        style={{ fontFamily: "'Unbounded', sans-serif", color: accent ? GOLD : '#fff' }}
      >
        {value}
      </p>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-center py-6 text-xs text-zinc-500">{children}</p>;
}

function BarChart({ items, labelKey, valueKey, formatLabel, showRevenue }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <div className="flex items-end gap-1 h-32" data-testid="bar-chart">
      {items.map((it, i) => {
        const h = ((it[valueKey] || 0) / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center min-w-0 group">
            <div className="text-[9px] text-zinc-500 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {it[valueKey]}{showRevenue && it.revenue ? ` · $${formatNumber(it.revenue)}` : ''}
            </div>
            <motion.div
              className="w-full rounded-t"
              style={{ background: `linear-gradient(180deg, ${GOLD}, #B8962E)` }}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ duration: 0.5, delay: i * 0.01 }}
              title={`${formatLabel(it[labelKey])} → ${it[valueKey]}${showRevenue ? ' · $' + formatNumber(it.revenue) : ''}`}
            />
            <div className="text-[8px] text-zinc-600 mt-1 truncate w-full text-center" style={{ writingMode: items.length > 18 ? 'vertical-rl' : 'horizontal-tb' }}>
              {formatLabel(it[labelKey])}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Demographics({ demo }) {
  if (!demo) return <Empty>Sin datos demográficos</Empty>;
  const genderEntries = Object.entries(demo.gender || {});
  const ageEntries = Object.entries(demo.age_ranges || {}).filter(([, v]) => v > 0);
  if (genderEntries.length === 0 && ageEntries.length === 0) {
    return <Empty>Los compradores aún no llenaron sus datos demográficos</Empty>;
  }
  return (
    <div className="space-y-4">
      {genderEntries.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Género</p>
          <div className="flex flex-wrap gap-2">
            {genderEntries.map(([k, v]) => (
              <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 border border-zinc-700">
                {prettyGender(k)} <strong className="text-white">{v}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
      {ageEntries.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Edad</p>
          <div className="space-y-1">
            {ageEntries.map(([k, v]) => {
              const tot = ageEntries.reduce((a, [, x]) => a + x, 0);
              const pct = tot ? Math.round((v / tot) * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-[11px] text-zinc-400 w-12">{k}</span>
                  <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: GOLD }} />
                  </div>
                  <span className="text-[11px] tabular-nums text-white w-10 text-right">{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TwoCols({ left, right }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {[left, right].map((col, i) => (
        <div key={i}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{col.title}</p>
          {Object.keys(col.data || {}).length === 0 ? (
            <p className="text-xs text-zinc-600">—</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(col.data).slice(0, 5).map(([k, v]) => (
                <li key={k} className="flex justify-between text-xs">
                  <span className="text-white truncate mr-2">{k}</span>
                  <span className="text-zinc-400 tabular-nums">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function formatNumber(n) {
  if (n == null) return '0';
  return Math.round(n).toLocaleString('es-AR');
}

function prettyGender(g) {
  const map = { male: 'Hombre', female: 'Mujer', other: 'Otro', m: 'H', f: 'M' };
  return map[g] || g;
}
